import React, {
  FormEvent,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAskAssistant } from '../../../../../hooks/useAskAssistant.hook';
import { useAssistantHistoryPagination } from '../../../../../hooks/useAssistantHistoryPagination.hook';
import { RoutesConstant } from '../../../../../constants/routes.constant';
import { LocationState } from '../../../../../types/locationState.interface';
import CrossIcon from '../../../../../components/Icons/Cross.icon';
import ChevronRightIcon from '../../../../../components/Icons/ChevronRight.icon';
import MessageIcon from '../../../../../components/Icons/Message.icon';
import SpinnerComponent from '../../../../../components/Spinner.component';
import TooltipComponent from '../../../../../components/Tooltip.component';
import {
  ASSISTANT_MESSAGE_ID_ATTR,
  getAssistantTurnAnchorMessageId,
  getBottomVisibleAssistantMessageId,
  readMiniChatScroll,
  writeMiniChatScroll,
} from '../../../../../utils/assistant.utils';
import {
  StyledAssistantTooltipWrapper,
  StyledHistoryLoader,
  StyledMessageBubble,
  StyledMessageRow,
  StyledUserMessageRow,
} from '../../../Assistant/Assistant.styles';
import AssistantTypingIndicator from '../../../Assistant/components/AssistantTypingIndicator.component';
import AssistantClearAllButton from '../../../Assistant/components/AssistantClearAllButton.component';
import AssistantMessageDeleteButton from '../../../Assistant/components/AssistantMessageDeleteButton.component';
import CandidateResultsList from '../../../Assistant/components/CandidateResultsList.component';
import {
  StyledMiniChatComposer,
  StyledMiniChatEmpty,
  StyledMiniChatHeader,
  StyledMiniChatHeaderActions,
  StyledMiniChatHeaderButton,
  StyledMiniChatInput,
  StyledMiniChatInputWrap,
  StyledMiniChatMessageColumn,
  StyledMiniChatMessages,
  StyledMiniChatPanel,
  StyledMiniChatSend,
  StyledMiniChatTitle,
} from './CandidatesMiniChat.styles';

interface CandidatesMiniChatProps {
  onClose: () => void;
}

const CandidatesMiniChat = ({ onClose }: CandidatesMiniChatProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { send, messages, isLoading } = useAskAssistant('mini-chat');
  const [question, setQuestion] = useState('');
  const messagesRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const savedScrollRef = useRef(readMiniChatScroll());
  const didRestoreScrollRef = useRef(false);
  const lastMessageId = messages[messages.length - 1]?.id;
  const turnAnchorMessageId = getAssistantTurnAnchorMessageId(messages);
  const shouldRestoreScroll =
    Boolean(savedScrollRef.current) &&
    savedScrollRef.current?.lastMessageId === lastMessageId;
  const { onScroll, loadingMore } = useAssistantHistoryPagination({
    containerRef: messagesRef,
    anchorRef: lastMessageRef,
    skipInitialScroll: shouldRestoreScroll,
  });

  useLayoutEffect(() => {
    if (didRestoreScrollRef.current || !shouldRestoreScroll) {
      return;
    }

    const container = messagesRef.current;
    const saved = savedScrollRef.current;

    if (!container || !saved) {
      return;
    }

    container.scrollTop = saved.scrollTop;
    didRestoreScrollRef.current = true;
  }, [shouldRestoreScroll, messages]);

  useLayoutEffect(() => {
    return () => {
      writeMiniChatScroll(messagesRef.current, lastMessageId);
    };
  }, [lastMessageId]);

  const persistScroll = () => {
    writeMiniChatScroll(messagesRef.current, lastMessageId);
  };

  const handleOpenAssistant = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    persistScroll();

    const focusMessageId = getBottomVisibleAssistantMessageId(
      messagesRef.current,
      lastMessageId,
    );
    const state: LocationState | undefined = focusMessageId
      ? { focusMessageId }
      : undefined;

    navigate(RoutesConstant.ASSISTANT, { state });
  };

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();

    if (!question.trim() || isLoading) {
      return;
    }

    send(question);
    setQuestion('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleMessagesScroll: React.UIEventHandler<HTMLDivElement> = event => {
    onScroll(event);
    writeMiniChatScroll(event.currentTarget, lastMessageId);
  };

  return (
    <StyledMiniChatPanel>
      <StyledMiniChatHeader>
        <StyledMiniChatTitle>{t('assistant.miniChatTitle')}</StyledMiniChatTitle>
        <StyledMiniChatHeaderActions>
          <StyledAssistantTooltipWrapper>
            <StyledMiniChatHeaderButton
              type="button"
              $icon
              onClick={handleOpenAssistant}
              aria-label={t('assistant.openInAssistant')}
            >
              <MessageIcon width={14} height={14} />
            </StyledMiniChatHeaderButton>
            <TooltipComponent tooltip={t('assistant.openInAssistant')} />
          </StyledAssistantTooltipWrapper>
          <AssistantClearAllButton variant="icon" />
          <StyledAssistantTooltipWrapper>
            <StyledMiniChatHeaderButton
              type="button"
              $icon
              onClick={onClose}
              aria-label={t('Close')}
            >
              <CrossIcon width={12} height={12} />
            </StyledMiniChatHeaderButton>
            <TooltipComponent tooltip={t('Close')} />
          </StyledAssistantTooltipWrapper>
        </StyledMiniChatHeaderActions>
      </StyledMiniChatHeader>

      <StyledMiniChatMessages ref={messagesRef} onScroll={handleMessagesScroll}>
        {loadingMore && (
          <StyledHistoryLoader>
            <SpinnerComponent size="small" />
          </StyledHistoryLoader>
        )}
        {!messages.length && !isLoading && (
          <StyledMiniChatEmpty>{t('assistant.miniChatWelcome')}</StyledMiniChatEmpty>
        )}
        {messages.map(message => {
          const hasCandidates =
            message.role === 'assistant' && !!message.candidates?.length;
          const assistantText = hasCandidates
            ? t('assistant.miniChatFound', {
                count: message.candidates?.length ?? 0,
              })
            : message.text;

          return (
            <StyledMessageRow
              key={message.id}
              ref={
                message.id === turnAnchorMessageId ? lastMessageRef : undefined
              }
              $isUser={message.role === 'user'}
              {...{ [ASSISTANT_MESSAGE_ID_ATTR]: message.id }}
            >
              {message.role === 'user' ? (
                <StyledUserMessageRow>
                  <AssistantMessageDeleteButton messageId={message.id} />
                  <StyledMessageBubble $isUser>
                    {assistantText}
                  </StyledMessageBubble>
                </StyledUserMessageRow>
              ) : (
                <StyledMiniChatMessageColumn $fullWidth>
                  <StyledMessageBubble>{assistantText}</StyledMessageBubble>
                  {hasCandidates && (
                    <CandidateResultsList
                      candidates={message.candidates ?? []}
                      variant="compact"
                      showOpenAssistant
                    />
                  )}
                  {!hasCandidates && message.status !== 'error' && (
                    <StyledMiniChatEmpty $inline>
                      {t('assistant.emptyCandidates')}
                    </StyledMiniChatEmpty>
                  )}
                </StyledMiniChatMessageColumn>
              )}
            </StyledMessageRow>
          );
        })}
        {isLoading && <AssistantTypingIndicator />}
      </StyledMiniChatMessages>

      <StyledMiniChatComposer onSubmit={handleSubmit}>
        <StyledMiniChatInputWrap>
          <StyledMiniChatInput
            value={question}
            onChange={event => setQuestion(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isLoading ? t('assistant.thinking') : t('assistant.placeholder')
            }
            rows={1}
            disabled={isLoading}
          />
          <StyledMiniChatSend
            type="submit"
            disabled={isLoading || !question.trim()}
            $isLoading={isLoading}
            aria-label={t('Send')}
          >
            <ChevronRightIcon width={14} height={14} />
          </StyledMiniChatSend>
        </StyledMiniChatInputWrap>
      </StyledMiniChatComposer>
    </StyledMiniChatPanel>
  );
};

export default CandidatesMiniChat;
